import React, { useState } from 'react';
import usePageTitle from 'src/utils/SetPageTitle';
import { ReactComponent as AccordianIcon } from 'src/Images/icons/common/accordion.svg';
import { ReactComponent as TabIcon } from 'src/Images/icons/common/tab.svg';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { CustomToolTip } from 'src/utils/CustomToolTip';
import TabCreateView from 'src/components/Layout/TabCreateView';
import AccordionCreateView from 'src/components/Layout/AccordionCreateView';
import getColorCode from 'src/utils/GetThemeColor';
import 'src/style/layout.css';
import BasicDetails from 'src/components/VehicleTrackingCreate/Inward/BasicDetails';
import AdditionalDetails from 'src/components/VehicleTrackingCreate/Inward/AdditionalDetails';
import { createVehicleTracking } from 'src/redux/Actions/VehicleTracking';
import Breadcrumb from 'src/components/Breadcrumb';
import { useSetBreadcrumbData } from 'src/utils/SetBreadcrumbData';

function CreateVehicleTrackingV2() {
  usePageTitle('Vehicle Tracking • Record Vehicle Tracking');
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const authReducer = useSelector((state) => state.AuthReducer);
  const themeColor = authReducer?.userData?.tenant?.theme;
  const organisationId = authReducer.userData.organisations?.id;
  const [toggleView, setToggleView] = useState(true);
  const [disableCreateButton, setDisableCreateButton] = useState(false);

  const handleToggle = () => {
    setToggleView((prevState) => !prevState);
  };

  // states
  const [formData, setFormData] = useState({
    vehicleNumber: null,
    plant: null,
    workflowId: null,
    vehicleTrackingType: '1',
    driverName: null,
    driverMobileNumber: null,
    driverLicenseNumber: null,
    licenseValidity: null,
    pucValidUpto: null,
    eWayBill: null,
    coa: null,
    containerNumber: null,
    originalInvoice: null,
    weightSlip: null,
    remarks: null,
    tankCondition: null,
    informationPanel: null,
    lidCondition: null,
    tyreCondition: null,
    tremCard: null,
    reflectors: null,
    safetyHelmet: null,
    safetyShoes: null,
    safetyHandgloves: null,
    safetyGoggles: null,
  });

  const [formErrors, setFormErrors] = useState({});
  const [selectedPurchaseOrder, setSelectedPurchaseOrder] = useState([]);

  // functions
  function handleOnChange(e) {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));

    // Clear error for this field if it was marked as error
    if (formErrors[name]) {
      setFormErrors((prev) => {
        const updated = { ...prev };
        delete updated[name];
        return updated;
      });
    }
  }

  const validateForm = () => {
    const errors = {};
    const form = document.querySelector('.create-form-container');
    const requiredFields = form.querySelectorAll(
      '[data-required-field="true"]',
    );

    requiredFields.forEach((field) => {
      const fieldName = field.getAttribute('name');
      const fieldValue = field.value;

      if (!fieldValue || fieldValue === '') {
        errors[fieldName] = true;
      }
    });

    // Set the current errors (clears ones that were fixed)
    setFormErrors(errors);

    // Return validity
    return Object.keys(errors).length === 0;
  };

  function handleVehicleTrackingCreate() {
    if (disableCreateButton) return; // Prevent multiple submissions

    const formIsValid = validateForm();
    if (!formIsValid) {
      return;
    }

    setDisableCreateButton(true); // Disable button

    const poListWithAdditionalDetails = selectedPurchaseOrder.map((poObj) => ({
      po_id: poObj.id,
    }));

    const createGateEntryPayload = {
      vehicle_number: formData.vehicleNumber,
      po: poListWithAdditionalDetails,
      plant_id: formData.plant,
      tenant_organisation_id: organisationId,
      driver_name: formData.driverName,
      driving_license_number: formData.driverLicenseNumber,
      safety_helmet: formData.safetyHelmet ?? false,
      safety_shoes: formData.safetyShoes ?? false,
      trem_card: formData.tremCard ?? false,
      safety_handgloves: formData.safetyHandgloves ?? false,
      safety_goggles: formData.safetyGoggles ?? false,
      driver_mobile_number: formData.driverMobileNumber,
      driver_license_validity: formData.licenseValidity,
      puc_valid_upto: formData.pucValidUpto,
      container_number: formData.containerNumber,
      tank_condition: formData.tankCondition,
      information_panel: formData.informationPanel,
      tyre_condition: formData.tyreCondition,
      lid_condition: formData.lidCondition,
      reflectors: formData.reflectors ?? false,
      test_report: formData.coa,
      weight_slip: formData.weightSlip,
      remarks: formData.remarks,
      workflow_id: formData.workflowId,
      // hardcoded to inward to maintain current flow
      direction: '1',
    };

    dispatch(createVehicleTracking(createGateEntryPayload))
      .then((response) => {
        const vehicleTrackingNumber = response?.vehicleTrackingNumber;
        if (vehicleTrackingNumber) {
          navigate(`/stations/`);
        }
      })
      .finally(() => {
        setDisableCreateButton(false); // Re-enable button
      });
  }

  const createVehicleTrackingComponents = [
    {
      id: 1,
      title: 'Basic Details',
      component: (
        <BasicDetails
          formData={formData}
          selectedPurchaseOrder={selectedPurchaseOrder}
          setSelectedPurchaseOrder={setSelectedPurchaseOrder}
          handleOnChange={(e) => handleOnChange(e)}
          formErrors={formErrors}
        />
      ),
    },
    {
      id: 2,
      title: 'Additional Details',
      component: (
        <AdditionalDetails
          formData={formData}
          handleOnChange={(e) => handleOnChange(e)}
        />
      ),
    },
  ];

  useSetBreadcrumbData(location.pathname, 'Record Vehicle Tracking');

  return (
    <form
      data-testid="create-vehicle-tracking-page"
      className="page-layout div-flex-column"
      onSubmit={(e) => e.preventDefault()}
    >
      <div className="div-flex-column">
        <div className="form-header-container div-flex-column">
          <div className="div-flex-column rg-5">
            <div className="div-flex-row left-padding cg-5">
              <Breadcrumb />
            </div>
          </div>
          <div className="form-header-container div-flex-row div-space-between border-bottom div-align-center w-100 right-padding">
            <div className="div-space-between slider-padding px-3">
              <span className="text-xxl text-bold">
                Record Vehicle Tracking
              </span>
            </div>
            <div className="div-flex-row toggle-button-container">
              <CustomToolTip title="Tab view" placement="bottom">
                <button
                  className={`toggle-button ${toggleView ? `theme-background-${themeColor} icon-color` : null}`}
                  onClick={handleToggle}
                  type="button"
                  data-testid="toggle-tab-view"
                  aria-label="Toggle tab view"
                >
                  <TabIcon
                    className="icon-size-lg"
                    color={getColorCode(themeColor)}
                  />
                </button>
              </CustomToolTip>
              <CustomToolTip title="Collapsible view" placement="bottom">
                <button
                  className={`toggle-button ${toggleView ? null : `theme-background-${themeColor} icon-color`}`}
                  onClick={handleToggle}
                  type="button"
                  aria-label="Toggle collapsible view"
                  data-testid="toggle-accordion-view"
                >
                  <AccordianIcon
                    className="icon-size-lg"
                    color={getColorCode(themeColor)}
                  />
                </button>
              </CustomToolTip>
            </div>
          </div>
        </div>
        <div
          className={`theme-scrollable-container-${themeColor} div-flex-column form-tab-container div-space-between`}
        >
          {toggleView ? (
            <TabCreateView
              createLeadFormComponents={createVehicleTrackingComponents}
            />
          ) : (
            <AccordionCreateView
              createLeadFormComponents={createVehicleTrackingComponents}
            />
          )}
          <div className="div-space-between footer-content-for-page">
            <button
              className={`common-btn-outlined theme-border-color-${themeColor}`}
              data-testid="cancel-btn"
            >
              Cancel
            </button>
            <div className="div-flex-row cg-20">
              <button
                data-testid="create-vehicle-btn"
                className={`common-btn theme-background-${themeColor}`}
                type="button"
                onClick={handleVehicleTrackingCreate}
                disabled={disableCreateButton}
              >
                {disableCreateButton
                  ? 'Creating...'
                  : 'Create Vehicle Tracking'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </form>
  );
}

export default CreateVehicleTrackingV2;
